<script setup lang="ts">
/* eslint-disable max-lines */
import { computed, onMounted, ref, watch } from 'vue'
import BuildingConstructionType from '@/components/inspection/BuildingConstructionType.vue'
import { constructionTypeLabels } from '@/constants/ui/inspection/buildingConstruction/buildingConstructionTypesLabel'
import type { ConstructionTypeType } from '@/types/ui/inspection/constructionTypeTypes'
import { useInspectionResultStore } from '@/stores/inspectionResultUpsertStore'
import LabelContainer from '@/components/inspection/common/LabelContainer.vue'
import CommonButton from '@/components/common/button/CommonButton.vue'
import { combineDateTimeToTimestamp, splitDatetime } from '@/utils/dateUtil/dateUtils.ts'
import { getInspectorList } from '@/accessors/sessionStorage/inspectorList'
import { CONSTRUCTION_TYPE } from '@/constants/accessor/api/emergencyInspectionResult/assessmentInfo/constructionType'
import { useCommonModalStore } from '@/stores/commonModalStore'
import PhotoDBOperator from '@/accessors/indexedDB/photoDBOperator'
import { useDisasterStore } from '@/stores/disasterStore'
import { defineRule, useField, useForm } from 'vee-validate'
import { z, ZodError } from 'zod'
import { MODAL_RESULT } from '@/modules/miiaFw/types/components/commonModal'
import { useUserAttributes } from '@/modules/miiaFw/composables/useUserAttributes'

const emit = defineEmits<{
  (event: 'updateUnfilledList', unfilledList: string[]): void
  (event: 'onValidationResult', isValid: boolean): void
  (event: 'savedInspectionResultStore'): void
}>()

const props = withDefaults(
  defineProps<{
    triggerValidate: boolean
    saveToStoreFlag: boolean
  }>(),
  {
    triggerValidate: false,
    saveToStoreFlag: false
  }
)

const { getUserAttributesFromStore } = useUserAttributes()
const inspectionResultStore = useInspectionResultStore()
const commonModalStore = useCommonModalStore()
const disasterStore = useDisasterStore()

/** 整理番号 */
const serialNumber = ref<string>('')
/** 調査日時 */
const inspectedDateTime = ref<string | null>(null)
/** 調査日 */
const inspectedDate = ref<string | null>(null)
/** 調査時間 */
const inspectedHour = ref<string | null>(null)
/** 調査回数 */
const inspectionCount = ref<string>('')

/** 建物構造の選択状態 */
const selectedBuildingConstructionType = ref<ConstructionTypeType>(CONSTRUCTION_TYPE.WOOD)

/** 必須項目の未入力リスト */
const unfilledList = ref<string[]>(['調査者氏名 1', '建物構造'])

/** 調査員氏名のバリデーション結果 */
const isInspectorValid = ref<boolean>(true)

/** 建物構造変更のウォッチャーを無効化するフラグ */
const isBuildingStructureWatcherDisabled = ref<boolean>(false)

/** 調査員氏名のバリデーション用のZodスキーマ */
const schema = z.array(z.string()).refine(values => values[0].trim() !== '')

/** 調査員氏名のバリデーションルール */
defineRule('inspectorValidation', (value: string[]) => {
  try {
    schema.parse(value)
    return true
  } catch (e) {
    if (e instanceof ZodError) {
      return e.errors[0].message
    }
    return false
  }
})
const { validate } = useForm()

/** 調査員リスト */
const inspectorList = useField<string[]>('inspectorList', 'inspectorValidation')

/** 復元可能な調査員情報が存在するか */
const hasRestoredInspectors = computed(() => {
  return getInspectorList().length > 0
})

/** 直前の調査から調査者を反映 */
const doRestore = (): void => {
  // 復元可能な調査員情報が存在しない場合は処理しない
  if (!hasRestoredInspectors.value) {
    return
  }
  const restoredInspectorList = getInspectorList()
  // ストアに反映
  inspectionResultStore.setAssessmentInfo(
    inspectionResultStore.assessmentInfo.serialNumber,
    inspectionResultStore.assessmentInfo.inspectedDatetime,
    inspectionResultStore.assessmentInfo.inspectionCount,
    restoredInspectorList
  )

  // フォーム側のフィールドに反映してUIを更新
  inspectorList.value.value = restoredInspectorList
}

/**
 * 建物構造の選択状態を更新する
 * @param constructionType 選択された建物構造タイプ
 * @returns なし
 */
const selectConstructionType = (constructionType: ConstructionTypeType) => {
  selectedBuildingConstructionType.value = constructionType
}

/**
 * 現在の日付と時間を抽出し、デフォルト値としてリアクティブ変数にセットする
 * @returns なし
 */
const setDefaultDateAndTime = (): void => {
  const currentDate = new Date()
  inspectedDate.value = currentDate.toISOString().split('T')[0]
  inspectedHour.value = String(currentDate.getHours())
}

/**
 * 全角数字を半角数字に変換する
 * @param str 変換対象の文字列
 * @returns 変換後の文字列
 */
const convertZenkakuNumberToHankaku = (str: string): string => {
  return str.replace(/[０-９]/g, s => String.fromCharCode(s.charCodeAt(0) - 0xFEE0))
}

/**
 * 調査回数入力欄のフォーカスアウト時に全角数字を半角数字に変換してセットする
 * @returns なし
 */
const onInspectionCountBlur = () => {
  inspectionCount.value = convertZenkakuNumberToHankaku(inspectionCount.value)
}

/**
 * Storeに保存されている inspectedDatetime を分解し、リアクティブ変数 inspectedDate と inspectedHour に設定する
 * @returns なし
 */
const setInspectedDateAndTimeFromStore = (): void => {
  const { date, hour } = splitDatetime(inspectionResultStore.assessmentInfo.inspectedDatetime)
  inspectedDate.value = date
  inspectedHour.value = hour !== null ? hour : null
}

/**
 * inspectedDate と inspectedHour を結合し inspectedDateTime を更新・セットする
 * @returns なし
 */
const updateInspectedDatetime = (): void => {
  inspectedDateTime.value = combineDateTimeToTimestamp(inspectedDate.value, inspectedHour.value)
}
/**
 * リアクティブ変数をStoreの値で初期化
 * @param なし
 */
const setRefsFromStore = async (): Promise<void> => {
  serialNumber.value = inspectionResultStore.assessmentInfo.serialNumber
  inspectedDateTime.value = inspectionResultStore.assessmentInfo.inspectedDatetime
  inspectionCount.value = inspectionResultStore.assessmentInfo.inspectionCount
  inspectorList.value.value = inspectionResultStore.assessmentInfo.inspectorList
  
  // selectedBuildingConstructionTypeのwatcherが起動するとすべてリセットされてしまうため、
  // 値が変更される（＝watcherが起動する）場合のみ、watcherを一時的に無効化する。
  if (selectedBuildingConstructionType.value !== inspectionResultStore.assessmentInfo.constructionType) {
    isBuildingStructureWatcherDisabled.value = true
    selectedBuildingConstructionType.value = inspectionResultStore.assessmentInfo.constructionType
  }

  if (!inspectorList.value.value.some(s => s !== '')) {
    // 調査者氏名が何も設定されていない → 新規調査
    if (hasRestoredInspectors.value) {
      // 復元可能な調査者氏名がある場合は復元
      inspectorList.value.value = getInspectorList()
    } else {
      // 復元可能な調査者氏名がない場合はログインユーザ名から調査者氏名設定
      const attribute = await getUserAttributesFromStore()
      inspectorList.value.value[0] = attribute.user_name
    }
  }

  setInspectedDateAndTimeFromStore()
}

watch(
  () => props.triggerValidate,
  async newValue => {
    if (newValue) {
      const isValid = await validate()
      isInspectorValid.value = isValid.valid
      unfilledList.value = isValid.valid ? [] : ['調査者氏名 1']
      emit('updateUnfilledList', unfilledList.value)
      emit('onValidationResult', isValid.valid)
    }
  }
)

watch(
  () => props.saveToStoreFlag,
  newValue => {
    if (newValue) {
      // 調査情報をStoreに保存
      inspectionResultStore.setAssessmentInfo(
        serialNumber.value,
        inspectedDateTime.value,
        inspectionCount.value,
        inspectorList.value.value
      )
      // 建物構造をStoreに保存
      inspectionResultStore.setConstructionType(selectedBuildingConstructionType.value)
      emit('savedInspectionResultStore')
    }
  }
)

watch(
  () => selectedBuildingConstructionType.value,
  (newValue, oldValue) => {
    // 自己ループを避けるためのチェック
    if (isBuildingStructureWatcherDisabled.value) {
      isBuildingStructureWatcherDisabled.value = false
      return
    }

    commonModalStore
      .openConfirmModal({
        title: '建物構造変更',
        body: '保存されていない入力内容、写真は削除されますがよろしいですか？',
        okTitle: 'はい',
        cancelTitle: 'いいえ'
      })
      .then(async result => {
        if (result === MODAL_RESULT.OK) {
          // indexedDBの写真データを削除（論理削除）
          const imageKeys = await PhotoDBOperator.queryPrefixMatchItems(
            [
              disasterStore.govCode!,
              disasterStore.disasterId!,
              inspectionResultStore.commonResultSummary.assessmentId
            ].join('/')
          )
          await Promise.all(
            imageKeys.map(async e => {
              if (e?.fileName === undefined) return
              await PhotoDBOperator.updateStateToDelete(e.fileName)
            })
          )

          // Storeのデータをリセット
          inspectionResultStore.resetExceptCommonData()
        }
      })
      .catch(async result => {
        if (result === MODAL_RESULT.CANCEL) {
          isBuildingStructureWatcherDisabled.value = true
          selectedBuildingConstructionType.value = oldValue
        }
      })
  }
)

/** 日付と時間が更新された際にinspectedDateTimeを更新 */
watch([inspectedDate, inspectedHour], updateInspectedDatetime)

onMounted(async () => {
  // Storeから各入力値を取得し、ref変数に反映
  await setRefsFromStore()

  // 新規調査の場合、調査IDを新規発行
  if (inspectionResultStore.commonResultSummary.assessmentId === '') {
    inspectionResultStore.setAssessmentId()
  }
  // デフォルトの日付と時間を設定
  if (!inspectedDateTime.value) {
    setDefaultDateAndTime()
  }
})
</script>

<template>
  <div class="container mt-5">
    <!-- 調査情報ヘッダー -->
    <div class="d-flex justify-content-between border-bottom border-dark mb-5 pb-4">
      <h1 class="font-weight-bold my-0">
        調査情報 <span class="text-danger">【入力必須項目あり】</span>
      </h1>
      <CommonButton
        @click="doRestore"
        :variant="hasRestoredInspectors ? 'outline-info' : 'secondary'"
        class="text-black fw-bold py-2 px-3 fs-4"
        :disabled="!hasRestoredInspectors"
      >
        直前の調査から調査者を反映する
      </CommonButton>
    </div>
    <!-- フォーム全体 -->
    <form @submit.prevent class="text-left">
      <!-- 整理番号 -->
      <LabelContainer name="name" label="整理番号" label-width="sm">
        <b-form-input v-model="serialNumber" class="border border-dark rounded px-2 py-1 fs-4" />
      </LabelContainer>
      <!-- 調査日時 -->
      <LabelContainer name="inspectedDate" label="調査日時" label-width="sm">
        <div class="d-flex align-items-center gap-2">
          <b-form-input
            id="inspectedDate"
            type="date"
            v-model="inspectedDate"
            class="border border-dark rounded p-2 w-75 fs-4"
          />
          <select
            id="inspectedHour"
            v-model="inspectedHour"
            class="border border-dark rounded p-2 fs-4"
          >
            <option :value="String(i)" v-for="i in 23" :key="i">{{ i }}時</option>
          </select>
        </div>
      </LabelContainer>
      <!-- 調査回数 -->
      <LabelContainer name="inspectionCount" label="調査回数" label-width="sm">
        <div class="d-flex items-center justify-content-end">
          <b-form-input
            id="inspectionCount"
            type="text"
            v-model="inspectionCount"
            class="bottom-border-input mx-2 w-25 fs-4"
            @blur="onInspectionCountBlur"
          />
          <span class="ml-2 fs-4">回目</span>
        </div>
      </LabelContainer>
      <!-- 調査者氏名 -->
      <div class="space-y-4">
        <LabelContainer
          v-for="(name, index) in inspectorList.value.value"
          :key="index"
          :label="`調査者氏名 ${index + 1}`"
          :name="`inspector_${index}`"
          label-width="sm"
        >
          <b-form-input
            v-model="inspectorList.value.value[index]"
            :id="`inspector_${index}`"
            type="text"
            maxlength="100"
            :class="[
              'border',
              'border-dark',
              'rounded',
              'px-2',
              'py-1',
              'fs-4',
              { 'input-required-empty': index === 0 && !isInspectorValid }
            ]"
          />
          <!-- 必須の赤文字を追加 -->
          <template #label>
            <span>{{ `調査者氏名 ${index + 1}` }}</span>
            <span v-if="index === 0" class="text-danger font-weight-bold">【必須】</span>
            <!-- indexが0のときのみ表示 -->
          </template>
        </LabelContainer>
      </div>
      <!-- アラートメッセージ -->
      <MiiaFwAlertMessage ref="alertMessage"></MiiaFwAlertMessage>
    </form>
    <!-- 建物構造ヘッダー -->
    <div class="mt-5">
      <div class="border-bottom border-dark pt-5 pb-4 mb-5">
        <h1 class="font-weight-bold my-0">建物構造 <span class="text-danger">【必須】</span></h1>
      </div>
      <div class="h3 mt-4">
        <BuildingConstructionType
          :structures="constructionTypeLabels"
          :selected-structure="selectedBuildingConstructionType"
          @select="selectConstructionType"
        />
      </div>
    </div>
  </div>
</template>

<style scoped>
.bottom-border-input {
  border: none;
  border-bottom: 2px solid black;
  text-align: center;
  border-radius: 0;
}
.input-required-empty {
  background-color: #ffcccc;
}
</style>
